/**
 * Frozen Penguin Pad product rules.
 * Settlement rail: Robinhood Chain. Access: permissionless, no KYC.
 * UI, contracts, and copy should follow these constants — do not restyle the economics ad hoc.
 */

export const PROTOCOL_CHAIN = 'Robinhood Chain'
export const PROTOCOL_QUOTE = 'USD'
export const PROTOCOL_ACCESS = 'permissionless' as const
export const REQUIRES_KYC = false

/** Linear curve: price = basePrice + increment * supply */
export const CURVE_FORMULA = 'price = basePrice + increment × supply'

/** Fraction of max supply that must sell on-curve before graduation. */
export const BOND_THRESHOLD = 0.85

export const LAUNCHPAD_ALLOCATION = {
  curvePct: 85,
  lpPct: 10,
  reservePct: 5,
} as const

/** Default tokens minted per 1 ETH contributed (18-decimal token units). */
export const PRESALE_DEFAULT_TOKENS_PER_ETH = 1_000_000

export const TEAM_VEST_MONTHS = 12
export const LIQUIDITY_LOCK_MONTHS = 6

/** ETH required to open a presale listing. */
export const PRESALE_LAUNCH_FEE_ETH = 0.1

/** Minimum share of remaining ETH that must seed liquidity. */
export const PRESALE_LP_MIN_PCT = 70

/** Maximum share of remaining ETH that can seed liquidity. */
export const PRESALE_LP_MAX_PCT = 100

/** Maximum creator take of remaining ETH after the platform fee. */
export const PRESALE_CREATOR_MAX_PCT = 30

/** Default LP / creator split of remaining ETH. */
export const PRESALE_LP_DEFAULT_PCT = 75
export const PRESALE_CREATOR_DEFAULT_PCT = 25

/** Protocol fee on launchpad buys and sells, in basis points (100 = 1%). */
export const LAUNCHPAD_FEE_BPS = 100

/** Platform fee taken from ETH raised on successful presales, in basis points (300 = 3%). */
export const PRESALE_FEE_BPS = 300

export const DEFAULT_MIN_CONTRIBUTION_USD = 10
export const DEFAULT_MAX_CONTRIBUTION_USD = 5_000

/** Default per-wallet contribution floor for ETH raises. */
export const DEFAULT_MIN_CONTRIBUTION_ETH = 0.01

/** Default per-wallet contribution ceiling for ETH raises. */
export const DEFAULT_MAX_CONTRIBUTION_ETH = 10

export const PRESALE_DEFAULT_SOFT_CAP_ETH = 5
export const PRESALE_DEFAULT_HARD_CAP_ETH = 20
/** Create-form defaults are temporarily lowered in defaultPresaleCreateValues() for testing.
 * Restore there to: soft 5, hard 20, min 0.01, max 10. Live factory minSoftCap() can still raise the floor. */
export const PRESALE_DEFAULT_DURATION_DAYS = 7
export const PRESALE_MAX_DURATION_DAYS = 30

/** Factory create floor: startsAt must be at least this far from now. */
export const PRESALE_START_BUFFER_MINUTES = 20
export const PRESALE_START_BUFFER_SECONDS = PRESALE_START_BUFFER_MINUTES * 60

export const PROTOCOL_RULES = {
  chain: PROTOCOL_CHAIN,
  quote: PROTOCOL_QUOTE,
  access: PROTOCOL_ACCESS,
  requiresKyc: REQUIRES_KYC,
  whoCanLaunch: 'Anyone with a Robinhood Chain wallet. No application queue and no KYC.',
  whoCanBuy: 'Anyone with a Robinhood Chain wallet. Penguin Pad does not geo-block or KYC buyers.',
  launchpad: {
    formula: CURVE_FORMULA,
    bondThreshold: BOND_THRESHOLD,
    allocation: LAUNCHPAD_ALLOCATION,
    buySellBeforeBond: true,
    afterBond: 'Curve trading stops. Remaining allocation seeds open-market LP; leftover goes to the colony reserve.',
    feeBps: LAUNCHPAD_FEE_BPS,
  },
  presale: {
    caps: 'Each raise publishes a creator-set soft cap and hard cap in ETH. Hard cap must be >= soft cap.',
    contributionLimits: 'The creator sets per-wallet min and max ETH. Effective max is the smaller of that max and remaining room to the hard cap. Contribute is only open during the start/end window. Remaining room below minContribution can only fill the hard cap as an exact leftover. Withdraw stays open until close, or after a creator cancel.',
    success: 'Raised >= soft cap by close time, then a wallet must click finalize() to launch. Hitting the hard cap does not auto-launch, but a wallet can click finalize() as soon as the hard cap is filled. After a successful launch, contributors claim tokens at the derived tokens-per-ETH rate (supply ÷ hard cap); amounts that would exceed the hard cap are rejected.',
    fail: 'Raised < soft cap at close. After finalize(), 100% of contributions are refundable. No tokens are issued.',
    claim: 'On success, contributors claim tokens at the sale rate. The creator receives an ETH share of remaining proceeds after the 3% platform fee (0–30%). Liquidity is locked at listing.',
    liquidityLockMonths: LIQUIDITY_LOCK_MONTHS,
    launchFeeEth: PRESALE_LAUNCH_FEE_ETH,
    feeBps: PRESALE_FEE_BPS,
    lpMinPct: PRESALE_LP_MIN_PCT,
    lpMaxPct: PRESALE_LP_MAX_PCT,
    creatorMaxPct: PRESALE_CREATOR_MAX_PCT,
    lpDefaultPct: PRESALE_LP_DEFAULT_PCT,
    creatorDefaultPct: PRESALE_CREATOR_DEFAULT_PCT,
    defaultMinUsd: DEFAULT_MIN_CONTRIBUTION_USD,
    defaultMaxUsd: DEFAULT_MAX_CONTRIBUTION_USD,
    defaultMinEth: DEFAULT_MIN_CONTRIBUTION_ETH,
    defaultMaxEth: DEFAULT_MAX_CONTRIBUTION_ETH,
    defaultSoftCapEth: PRESALE_DEFAULT_SOFT_CAP_ETH,
    defaultHardCapEth: PRESALE_DEFAULT_HARD_CAP_ETH,
    defaultDurationDays: PRESALE_DEFAULT_DURATION_DAYS,
  },
} as const

export function formatBps(bps: number): string {
  const pct = bps / 100
  return Number.isInteger(pct) ? `${pct}%` : `${pct.toFixed(2)}%`
}

export function clampPresaleLpPct(lpPct: number): number {
  const rounded = Math.round(Number.isFinite(lpPct) ? lpPct : PRESALE_LP_DEFAULT_PCT)
  return Math.min(PRESALE_LP_MAX_PCT, Math.max(PRESALE_LP_MIN_PCT, rounded))
}

export function creatorPctFromLp(lpPct: number): number {
  return 100 - clampPresaleLpPct(lpPct)
}

export function splitPresaleProceeds(raisedEth: number, lpPct: number) {
  const safeRaised = Number.isFinite(raisedEth) && raisedEth > 0 ? raisedEth : 0
  const clampedLp = clampPresaleLpPct(lpPct)
  const creatorPct = 100 - clampedLp
  const platform = safeRaised * (PRESALE_FEE_BPS / 10_000)
  const remaining = safeRaised - platform
  return {
    raisedEth: safeRaised,
    platformEth: platform,
    remainingEth: remaining,
    lpPct: clampedLp,
    creatorPct,
    lpEth: remaining * (clampedLp / 100),
    creatorEth: remaining * (creatorPct / 100),
  }
}
